import { t } from "../i18n/runtime";
import { useState } from "react";
import type { ReactNode } from "react";
import { useGameStore } from "../stores/gameStore";
import { Button } from "./primitives";

/**
 * The briefing: three cards, over a held board.
 *
 * It sits in the middle of the screen behind its own veil rather than in a
 * corner of the map. A corner card competes with the board for the same glance
 * and loses — the player skips it and then does not know what the game is. The
 * veil is what makes it unskippable-by-accident, and closing it is what starts
 * the war: the session is created held, so the opening position is on screen to
 * be read while the clock has not moved. Closing it hands off to the company
 * identity gate; that gate starts the war only after the browser fixes a name
 * (in persistent storage when available, otherwise in this tab's memory).
 *
 * It never names a state. The roster changes, copy that spells it out goes
 * stale silently, and nothing here needs a name to say what the loop is —
 * `tests/nationNames.test.ts` holds that line.
 */

/** `*emphasis*`, so the copy stays one readable string per card. */
export function highlight(body: string): ReactNode[] {
  return body.split(/\*([^*]+)\*/g).map((part, index) =>
    index % 2 === 1 ? (
      <em className="tutorial__key" key={index}>
        {part}
      </em>
    ) : (
      part
    ),
  );
}

const STEPS: Array<{ title: string; body: string }> = [
  {
    get title() {
      return t("Tutorial.the_arms_broker_behind_seven_warring_nations");
    },
    get body() {
      return t("Tutorial.each_of_the_seven_nations_uses_a");
    },
  },
  {
    get title() {
      return t("Tutorial.only_weapons_and_resources_are_traded");
    },
    get body() {
      return t(
        "Tutorial.resource_facilities_mine_resources_and_weapons_factories",
      );
    },
  },
  {
    get title() {
      return t("Tutorial.keep_the_balance_and_earn_as_much");
    },
    get body() {
      return t("Tutorial.if_one_nation_takes_the_continent_no");
    },
  },
];

export function Tutorial() {
  const dismissed = useGameStore((s) => s.tutorialDismissed);
  const dismissTutorial = useGameStore((s) => s.dismissTutorial);
  const [step, setStep] = useState(0);

  if (dismissed) return null;
  const current = STEPS[step];
  if (!current) return null;
  const last = step === STEPS.length - 1;

  // The held round stays at tick zero. CompanyNameGate is the next and only
  // owner of the start request, after the installation identity is fixed.
  const close = (): void => {
    dismissTutorial();
  };

  return (
    <div className="tutorial-veil">
      <aside className="tutorial" role="dialog" aria-modal="true">
        <div className="label">
          {t("Tutorial.briefing_step", { p0: step + 1, p1: STEPS.length })}
        </div>
        <h2 className="tutorial__title">{current.title}</h2>
        <p className="tutorial__step">{highlight(current.body)}</p>
        <div className="tutorial__foot">
          <div className="tutorial__dots">
            {STEPS.map((entry, index) => (
              <span
                key={entry.title}
                className={
                  index === step
                    ? "tutorial__dot tutorial__dot--on"
                    : "tutorial__dot"
                }
              />
            ))}
          </div>
          <div className="tutorial__buttons">
            <Button onClick={close}>{t("Tutorial.skip")}</Button>
            <Button
              tone="primary"
              onClick={last ? close : () => setStep(step + 1)}
            >
              {last ? t("Tutorial.start") : t("Tutorial.next")}
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}
